import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Check, Pencil, Settings as SettingsIcon } from 'lucide-react';
import { Input } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 설정에서 저장된 값이 있으면 "✓ 설정의 키 사용 중 · 변경" 미니 박스로 표시.
 * 저장된 값 없거나 사용자가 "변경" 누르면 입력 필드 노출.
 *
 * password 토글 지원 (Gemini 키 같은 시크릿용).
 */
export function PrefilledKeyField({
  label,
  storedValue,
  value,
  onChange,
  placeholder,
  secret = false,
  helpText,
}: {
  label: string;
  /** 설정 페이지에 저장된 원본 값 (prefs) — 있으면 "저장됨" 박스 표시 */
  storedValue: string;
  /** 현재 입력 박스의 값 */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** true 면 password type + Eye 토글 */
  secret?: boolean;
  helpText?: React.ReactNode;
}) {
  const usingStored = !!storedValue && storedValue === value;
  const [editing, setEditing] = useState(!usingStored);
  const [show, setShow] = useState(false);

  function startEdit() {
    setEditing(true);
  }
  function revertToStored() {
    onChange(storedValue);
    setEditing(false);
  }

  // 저장된 값과 동일하고 편집 모드 아니면 → 미니 박스 표시
  const showBox = usingStored && !editing;

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{label}</span>
        {!storedValue && (
          <Link
            to="/settings"
            className="text-[11px] text-gold hover:underline inline-flex items-center gap-1"
          >
            <SettingsIcon className="h-3 w-3" /> 설정에서 미리 저장
          </Link>
        )}
      </div>

      {showBox ? (
        <div className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-emerald-400/40 bg-emerald-400/5 px-3 py-2 text-sm">
          <span className="inline-flex items-center gap-2 text-emerald-200">
            <Check className="h-4 w-4 shrink-0" />
            <span className="font-medium">
              설정에 저장된 값 사용 중
              {secret ? '' : ` — ${truncate(storedValue, 40)}`}
            </span>
          </span>
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1 rounded-md border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-200 hover:bg-gray-800"
          >
            <Pencil className="h-3 w-3" /> 변경
          </button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Input
            className={cn(secret && 'font-mono pr-9')}
            type={secret ? (show ? 'text' : 'password') : 'text'}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {secret && (
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gold"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
          {storedValue && storedValue !== value && (
            <button
              type="button"
              onClick={revertToStored}
              className="absolute right-10 top-1/2 -translate-y-1/2 text-[11px] text-gold hover:underline"
            >
              저장된 값 복원
            </button>
          )}
        </div>
      )}
      {helpText && <p className="mt-1 text-[11px] text-gray-500">{helpText}</p>}
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}
